// ─────────────────────────────────────────────────────────────────────────────
// IMPORTING A CONTACT LIST INTO PARTNER LEADS.
//
// 🚨 THE ENTIRE BACKEND FOR THIS SHIPPED AND NOTHING EVER RENDERED A WAY IN.
// POST /admin/partner-leads/import/stage and .../import/:id/commit have been
// live — staging, per-row resolution against partners and existing leads, the
// uploader attestation, a transactional commit — and the Leads page had no
// Import button. `imported` was a source value nothing could produce.
//
// 🔑 THE ROWS ARE PARSED HERE, CLIENT-SIDE, AND POSTED AS JSON. The server takes
// a mapped array rather than a file, deliberately: one parser to reason about
// and no spreadsheet library in the request path. What the server owns is
// resolution and writing, which is where the decisions are.
//
// ⚠️ FOUR STEPS, AND THE PREVIEW IS NOT SKIPPABLE. Nothing lands in partner_leads
// until the AE has seen what will land — a four-hundred-row spreadsheet that
// half-commits is worse than one that refuses, because the uploader cannot tell
// which half made it and re-uploading doubles the part that did.
//
// ⚠️ AND THE ATTESTATION IS READ BEFORE THE COMMIT BUTTON EXISTS. It is the only
// consent artefact this path will ever have — an imported contact never gave
// Doorly anything — so it is a statement by the uploader about the relationship,
// and it is shown as five separate clauses rather than one paragraph nobody
// reads. Clause 5 is about the uploader's own obligations to a former employer,
// which is the one that matters most and the one a wall of text would bury.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// The fields the server reads off a staged row. `raw` carries everything else.
// 🚨 A NAME ARRIVES AS ONE COLUMN OR TWO, AND MOST EXPORTS SEND TWO. The first
// version offered only "Name", so a CSV with First name / Last name columns
// could map neither — a rep would have had to point Name at the first-name
// column and lose every surname, silently, on the whole list.
//
// ⚠️ ALL THREE ARE OFFERED AND THE JOIN HAPPENS HERE. The server's staged row
// takes a single `name`, and teaching it about two shapes would put the join in
// two places. What it receives is the same either way.
const FIELDS = [
  { key: 'name',         label: 'Full name', hints: ['name', 'full name', 'contact', 'contact name', 'display name'] },
  { key: 'first_name',   label: 'First name', hints: ['first name', 'first', 'firstname', 'given name', 'fname'] },
  { key: 'last_name',    label: 'Last name',  hints: ['last name', 'last', 'lastname', 'surname', 'family name', 'lname'] },
  { key: 'email',        label: 'Email',     hints: ['email', 'e-mail', 'email address'] },
  { key: 'phone',        label: 'Phone',     hints: ['phone', 'mobile', 'cell', 'telephone', 'phone number'] },
  { key: 'company',      label: 'Company',   hints: ['company', 'firm', 'brokerage', 'office', 'organisation', 'organization'] },
  { key: 'partner_type', label: 'Type',      hints: ['type', 'partner type', 'role'] },
  { key: 'level',        label: 'Level',     hints: ['level', 'seniority'] },
];

const OUTCOME_TONE = {
  new:               { bg: '#f4fee8', color: '#2d6a0a', label: 'Will be created' },
  duplicate_lead:    { bg: '#fffbeb', color: '#7a5200', label: 'Already a lead' },
  already_a_partner: { bg: '#eff6ff', color: '#2854c0', label: 'Already a partner' },
  pending_invite:    { bg: '#f5f3ff', color: '#6d28d9', label: 'Already invited' },
  invalid:           { bg: '#fff0f3', color: '#ff082f', label: 'Cannot import' },
};

// ── 🚨 A CSV PARSER THAT HANDLES QUOTED COMMAS ──────────────────────────────
//
// `line.split(',')` is the obvious version and it is wrong for exactly the file
// people import: "Caballero, Joaquin" becomes two columns and every field after
// it shifts by one, silently, for that row only. Quoted fields, escaped quotes
// ("" inside a quoted field) and CRLF are handled; anything stranger than that
// is a spreadsheet the AE should export again.
function parseCsv(text) {
  const rows = [];
  let row = [], field = '', inQuotes = false;
  const src = String(text).replace(/^﻿/, '');  // strip a BOM Excel adds
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else field += c;
      continue;
    }
    if (c === '"') { inQuotes = true; continue; }
    if (c === ',') { row.push(field); field = ''; continue; }
    if (c === '\r') continue;
    if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; continue; }
    field += c;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((cell) => String(cell).trim() !== ''));
}

// Best guess at which column is which, by header name. A guess the AE can
// override — never one applied silently.
// ⚠️ THE MOST SPECIFIC HINT WINS, AND ORDER ALONE DOES NOT GIVE THAT. "Name"
// and "First name" are both exact hints in their own lists, but a header of
// "First name" must never be claimed by the `name` field — which is what
// iterating FIELDS in order would do, since `name` comes first. So the pass runs
// most-specific-first: every field whose hint matches the header EXACTLY is
// resolved by longest hint, and a column is claimed once.
function guessMap(headers) {
  const out = {};
  const used = new Set();
  const candidates = [];
  headers.forEach((h, i) => {
    const norm = String(h || '').trim().toLowerCase();
    for (const f of FIELDS) {
      if (f.hints.includes(norm)) {
        candidates.push({ key: f.key, index: i, specificity: norm.length });
      }
    }
  });
  candidates.sort((a, b) => b.specificity - a.specificity);
  for (const c of candidates) {
    if (out[c.key] !== undefined || used.has(c.index)) continue;
    out[c.key] = c.index;
    used.add(c.index);
  }
  return out;
}

export default function LeadImportModal({ onClose, onCommitted }) {
  const token = localStorage.getItem('doorly_admin_token');
  const [step, setStep] = useState('upload');   // upload → map → preview
  const [filename, setFilename] = useState('');
  const [headers, setHeaders] = useState([]);
  const [bodyRows, setBodyRows] = useState([]);
  const [map, setMap] = useState({});
  const [staged, setStaged] = useState(null);
  const [attested, setAttested] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const mapped = useMemo(() => bodyRows.map((cells) => {
    const raw = {};
    headers.forEach((h, i) => { raw[String(h || `col${i}`)] = cells[i] ?? ''; });
    const pick = (k) => (map[k] === undefined || map[k] === '' ? '' : String(cells[map[k]] ?? '').trim());
    // ⚠️ A MAPPED "Full name" WINS OVER THE PAIR. A file carrying all three is
    // rare, but where it does the single column is the one somebody curated —
    // and joining the pair on top of it would produce "Jane Smith Jane Smith".
    const joined = [pick('first_name'), pick('last_name')].filter(Boolean).join(' ');
    // 🔑 company RIDES IN `raw`, which is what the commit reads it from. The
    // server's staged-row columns are a fixed set; raw is the whole row, and
    // putting the firm there means no migration to carry one more field.
    return {
      name: pick('name') || joined,
      email: pick('email'), phone: pick('phone'),
      partner_type: pick('partner_type') || null, level: pick('level') || null,
      raw: { ...raw, company: pick('company') },
    };
  }), [bodyRows, headers, map]);

  const onFile = (file) => {
    setError(null);
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const rows = parseCsv(reader.result);
      if (rows.length < 2) { setError('That file has no data rows under its header.'); return; }
      // ⚠️ THE CEILING IS THE SERVER'S, SAID HERE. An import is a person's
      // contact list, not a data migration — a file an order of magnitude bigger
      // is somebody pasting a purchased database, and learning that after a long
      // upload is learning it in the worst place.
      if (rows.length - 1 > 1000) {
        setError(`That file has ${rows.length - 1} rows. Imports are limited to 1,000.`);
        return;
      }
      setFilename(file.name);
      setHeaders(rows[0]);
      setBodyRows(rows.slice(1));
      setMap(guessMap(rows[0]));
      setStep('map');
    };
    reader.onerror = () => setError('That file could not be read.');
    reader.readAsText(file);
  };

  const stage = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/api/admin/partner-leads/import/stage`,
        { rows: mapped, filename, column_map: map },
        { headers: { Authorization: `Bearer ${token}` } });
      setStaged(data);
      setStep('preview');
    } catch (e) {
      setError(e.response?.data?.error || 'The file could not be staged.');
    } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/partner-leads/import/${staged.import_id}/commit`,
        { attested: true }, { headers: { Authorization: `Bearer ${token}` } });
      onCommitted?.(data);
    } catch (e) {
      setError(e.response?.data?.error || 'The import could not be committed.');
      setBusy(false);
    }
  };

  const box = { background: '#fff', borderRadius: 14, width: 'min(760px, 96vw)', maxHeight: '90vh',
    overflowY: 'auto', padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
  const sel = { padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, background: '#fff' };
  const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #f3f4f6' };
  const td = { padding: '8px 10px', fontSize: 12.5, color: '#111827', borderBottom: '1px solid #f9fafb' };

  const counts = staged?.counts || {};
  const willCreate = counts.new || 0;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={box}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#111827' }}>Import leads</h2>
            <p style={{ margin: '3px 0 0', fontSize: 12.5, color: '#6b7280' }}>
              A CSV of your own professional contacts. Nothing is written until you have seen what will land.
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: '#9ca3af', cursor: 'pointer' }}>×</button>
        </div>

        {error && (
          <div style={{ margin: '12px 0', padding: '10px 13px', background: '#fff0f3', color: '#ff082f',
            fontSize: 13, borderRadius: 8 }}>{error}</div>
        )}

        {/* ── 1. upload ──────────────────────────────────────────────────── */}
        {step === 'upload' && (
          <div style={{ marginTop: 18 }}>
            <label style={{ display: 'block', border: '2px dashed #d1d5db', borderRadius: 12,
              padding: '34px 20px', textAlign: 'center', cursor: 'pointer', background: '#fcfcfd' }}>
              <input type="file" accept=".csv,text/csv" style={{ display: 'none' }}
                onChange={(e) => onFile(e.target.files?.[0])} />
              <div style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>Choose a CSV file</div>
              <div style={{ fontSize: 12.5, color: '#9ca3af', marginTop: 5 }}>
                Name (or First + Last), Email, Phone, Company — a header row, up to 1,000 contacts
              </div>
            </label>
          </div>
        )}

        {/* ── 2. map ─────────────────────────────────────────────────────── */}
        {step === 'map' && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 13, color: '#374151', marginBottom: 12 }}>
              <strong>{filename}</strong> — {bodyRows.length} row{bodyRows.length === 1 ? '' : 's'}.
              {/* ⚠️ THE GUESS IS SHOWN AS A GUESS. Silently mapping by header name
                  is right most of the time and invisible when it is wrong. */}
              {' '}We have guessed the columns below; correct anything that is off.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 10 }}>
              {FIELDS.map((f) => (
                <label key={f.key} style={{ display: 'block' }}>
                  <span style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: '#6b7280', marginBottom: 4 }}>
                    {f.label}
                  </span>
                  <select value={map[f.key] ?? ''} style={{ ...sel, width: '100%' }}
                    onChange={(e) => setMap((p) => ({ ...p, [f.key]: e.target.value === '' ? '' : Number(e.target.value) }))}>
                    <option value="">— not in this file —</option>
                    {headers.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                  </select>
                </label>
              ))}
            </div>

            <div style={{ marginTop: 16, fontSize: 11.5, fontWeight: 700, color: '#9ca3af',
              textTransform: 'uppercase', letterSpacing: '0.04em' }}>First few rows</div>
            <div style={{ overflowX: 'auto', border: '1px solid #f3f4f6', borderRadius: 10, marginTop: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                {/* ⚠️ THE RESOLVED NAME, NOT THE THREE COLUMNS THAT MADE IT.
                    The preview's job is to show what will LAND, and a rep who
                    mapped First and Last needs to see them joined — that is the
                    step most likely to be wrong. */}
                <thead><tr>
                  <th style={th}>Name</th>
                  {FIELDS.filter((f) => !['name', 'first_name', 'last_name'].includes(f.key))
                    .map((f) => <th key={f.key} style={th}>{f.label}</th>)}
                </tr></thead>
                <tbody>
                  {mapped.slice(0, 4).map((r, i) => (
                    <tr key={i}>
                      <td style={td}>{r.name || <span style={{ color: '#d1d5db' }}>—</span>}</td>
                      <td style={td}>{r.email || <span style={{ color: '#d1d5db' }}>—</span>}</td>
                      <td style={td}>{r.phone || <span style={{ color: '#d1d5db' }}>—</span>}</td>
                      <td style={td}>{r.raw.company || <span style={{ color: '#d1d5db' }}>—</span>}</td>
                      <td style={td}>{r.partner_type || <span style={{ color: '#d1d5db' }}>—</span>}</td>
                      <td style={td}>{r.level || <span style={{ color: '#d1d5db' }}>—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
              <button type="button" onClick={() => { setStep('upload'); setError(null); }}
                style={{ ...sel, cursor: 'pointer', fontWeight: 600 }}>Back</button>
              <button type="button" disabled={busy || map.email === undefined || map.email === ''}
                onClick={stage}
                style={{ padding: '9px 18px', borderRadius: 9, border: 'none',
                  background: (map.email === undefined || map.email === '') ? '#e5e7eb' : '#3a6ad9',
                  color: (map.email === undefined || map.email === '') ? '#9ca3af' : '#fff',
                  fontSize: 13, fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>
                {busy ? 'Checking…' : 'Check these against the book'}
              </button>
            </div>
            {/* ⚠️ THE REASON THE BUTTON IS DEAD IS SAID, not left to be discovered.
                Every resolution the server does keys on the email address. */}
            {(map.email === undefined || map.email === '') && (
              <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 6, textAlign: 'right' }}>
                Map the email column first — it is how we match against people already in the book.
              </div>
            )}
            {/* ⚠️ A WARNING, NOT A BLOCK. A list with no name column is still
                importable — the email is what matters for resolution — but a
                rep who mapped First and forgot Last should see it here rather
                than discover it as 400 leads with no surname. */}
            {map.email !== undefined && map.email !== ''
              && (map.name === undefined || map.name === '')
              && (map.last_name === undefined || map.last_name === '')
              && (map.first_name !== undefined && map.first_name !== '') && (
              <div style={{ fontSize: 11.5, color: '#92400e', marginTop: 6, textAlign: 'right' }}>
                Only a first name is mapped — these leads will import without surnames.
              </div>
            )}
          </div>
        )}

        {/* ── 3. preview + attestation ───────────────────────────────────── */}
        {step === 'preview' && staged && (
          <div style={{ marginTop: 18 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
              {Object.entries(OUTCOME_TONE).map(([key, tone]) => (
                (counts[key] || 0) > 0 ? (
                  <span key={key} style={{ padding: '5px 11px', borderRadius: 8, fontSize: 12.5,
                    fontWeight: 700, background: tone.bg, color: tone.color }}>
                    {counts[key]} {tone.label.toLowerCase()}
                  </span>
                ) : null
              ))}
            </div>

            <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #f3f4f6', borderRadius: 10 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={th}>Row</th><th style={th}>Contact</th><th style={th}>Outcome</th><th style={th}>Why</th>
                </tr></thead>
                <tbody>
                  {(staged.rows || []).map((r) => {
                    const tone = OUTCOME_TONE[r.outcome] || OUTCOME_TONE.invalid;
                    return (
                      <tr key={r.id}>
                        <td style={{ ...td, color: '#9ca3af' }}>{r.row_number}</td>
                        <td style={td}>
                          <div style={{ fontWeight: 600 }}>{r.name || '—'}</div>
                          <div style={{ fontSize: 11.5, color: '#6b7280' }}>{r.email || r.phone || ''}</div>
                        </td>
                        <td style={td}>
                          <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11.5,
                            fontWeight: 700, background: tone.bg, color: tone.color }}>{tone.label}</span>
                        </td>
                        <td style={{ ...td, color: '#6b7280' }}>{r.match_detail || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* 🚨 THE ATTESTATION, AS CLAUSES. It is the only consent artefact this
                path will ever have — an imported contact never gave Doorly
                anything — so it is a statement by the uploader about the
                relationship, and each clause is read separately rather than as one
                paragraph. */}
            <div style={{ marginTop: 16, border: '1px solid #e5e7eb', borderRadius: 10, padding: '13px 14px', background: '#fcfcfd' }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase',
                letterSpacing: '0.04em', marginBottom: 8 }}>
                Before importing, you are confirming
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: '#374151', lineHeight: 1.65 }}>
                {(staged.attestation?.clauses || []).map((c, i) => <li key={i}>{c}</li>)}
              </ul>
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 11, cursor: 'pointer' }}>
                <input type="checkbox" checked={attested} style={{ marginTop: 2 }}
                  onChange={(e) => setAttested(e.target.checked)} />
                <span style={{ fontSize: 12.5, color: '#111827', fontWeight: 600 }}>
                  All of the above is true of this list.
                </span>
              </label>
              {/* ⚠️ THE VERSION IS RECORDED, NOT DISPLAYED. It is snapshotted onto
                  the batch row with the text and its hash, so "which wording did
                  this rep attest to" stays answerable — but an internal label
                  reading "2026-09-v1-DRAFT" under a paragraph somebody is about
                  to sign invites the question of what the other drafts said, and
                  the answer is not one the AE needs at that moment. */}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18 }}>
              <button type="button" onClick={() => { setStep('map'); setStaged(null); setAttested(false); }}
                style={{ ...sel, cursor: 'pointer', fontWeight: 600 }}>Back</button>
              <button type="button" disabled={!attested || busy || willCreate === 0} onClick={commit}
                style={{ padding: '9px 18px', borderRadius: 9, border: 'none',
                  background: (!attested || willCreate === 0) ? '#e5e7eb' : '#3a6ad9',
                  color: (!attested || willCreate === 0) ? '#9ca3af' : '#fff',
                  fontSize: 13, fontWeight: 700, cursor: (!attested || willCreate === 0) ? 'not-allowed' : 'pointer' }}>
                {busy ? 'Importing…' : `Import ${willCreate} lead${willCreate === 1 ? '' : 's'}`}
              </button>
            </div>
            {willCreate === 0 && (
              <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 6, textAlign: 'right' }}>
                Nothing in this file is new — everyone is already a lead, a partner, or invited.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
